import { AfterViewInit, Directive, ElementRef, OnDestroy, inject } from '@angular/core';

@Directive({
  selector: '[dojoStemFirstLineAlign]',
  standalone: true,
})
export class StemFirstLineAlignDirective implements AfterViewInit, OnDestroy {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private mutationObserver?: MutationObserver;
  private timeoutIds: number[] = [];

  ngAfterViewInit(): void {
    // Initial alignment with staggered attempts for LaTeX rendering
    this.scheduleAlignment([100, 500]);
    this.setupObserver();
  }

  ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
    this.timeoutIds.forEach((id) => clearTimeout(id));
    this.timeoutIds = [];
  }

  private scheduleAlignment(delays: number[]): void {
    delays.forEach((delay) => {
      const timeoutId = window.setTimeout(() => this.alignStemLabel(), delay);
      this.timeoutIds.push(timeoutId);
    });
  }

  private setupObserver(): void {
    this.mutationObserver = new MutationObserver(() => {
      this.scheduleAlignment([50]);
    });

    this.mutationObserver.observe(this.elementRef.nativeElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  private alignStemLabel(): void {
    const stemLabel = this.findStemLabel();
    if (!stemLabel) {
      return;
    }

    const firstLineHeight = this.calculateFirstLineHeight();
    const LABEL_HEIGHT = 28;

    const offset = firstLineHeight > LABEL_HEIGHT ? (firstLineHeight - LABEL_HEIGHT) / 2 : 0;

    stemLabel.style.marginTop = `${offset}px`;
  }

  private findStemLabel(): HTMLElement | null {
    const container = this.elementRef.nativeElement.closest('.draggable-item');
    if (!container) {
      return null;
    }

    const element = container.querySelector('dojo-stem-label span') as HTMLElement;
    return element?.textContent?.match(/^\([a-z]\)$/i) ? element : null;
  }

  private calculateFirstLineHeight(): number {
    const element = this.elementRef.nativeElement;

    // Primary: Look for KaTeX (now with fixed margins)
    const katex = element.querySelector('.katex-display .katex, .katex') as HTMLElement;
    if (katex?.offsetHeight > 0) {
      return katex.offsetHeight;
    }

    // Fallback: First content element
    const content = element.querySelector('p, div:not([class*="w-full"])') as HTMLElement;
    if (content?.offsetHeight > 0) {
      return content.offsetHeight;
    }

    // Final fallback: Computed line height
    const { fontSize, lineHeight } = getComputedStyle(element);
    const size = parseFloat(fontSize);

    if (lineHeight === 'normal') {
      return size * 1.2;
    }
    if (lineHeight.endsWith('px')) {
      return parseFloat(lineHeight);
    }

    const multiplier = parseFloat(lineHeight);
    return !isNaN(multiplier) ? size * multiplier : size * 1.2;
  }
}
